/**
 * Cảnh báo đã kích hoạt theo quy tắc `audit.alert_rules` (ADR-0059 mục 4.1) —
 * `GET /api/v1/tenants/{id}/audit/alerts` và `POST …/{alert_id}/ack` của
 * audit-service.
 */
import type { MucDoThongBao } from './types.ts';
import type { NhomTheo } from './quy-tac-canh-bao.ts';

export type TrangThaiCanhBao = 'OPEN' | 'ACKNOWLEDGED';

/** `AuditAlert` — giữ đúng tên trường của API. */
export interface CanhBaoAudit {
  id: string;
  tenant_id: string;
  rule_id: string;
  rule_name: string;
  severity: MucDoThongBao;
  group_by: NhomTheo;
  /** UUID user, IP đã che (`203.0.113.x`), hoặc `*` khi gom cả tenant. */
  group_key: string;
  /** Tên người khi `group_by` là `user`; `null` khi đã rời tổ chức hoặc danh bạ đang lỗi. */
  group_name?: string | null;
  event_count: number;
  threshold: number;
  window_seconds: number;
  window_start: string;
  window_end: string;
  sample_event_ids: string[];
  status: TrangThaiCanhBao;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  created_at: string;
}

/** `AuditAlertPage`. */
export interface TrangCanhBao {
  items: CanhBaoAudit[];
  next_cursor: string | null;
}

/** Bộ lọc của danh sách; `null` là không lọc. */
export interface BoLocCanhBao {
  severity: MucDoThongBao | null;
  status: TrangThaiCanhBao | null;
}

export const BO_LOC_TRONG: BoLocCanhBao = { severity: null, status: null };

/** Đang lọc thì rỗng là "rỗng vì bộ lọc": bỏ mô tả và lời mời hành động. */
export function coBoLoc(b: BoLocCanhBao): boolean {
  return b.severity !== null || b.status !== null;
}

/** Nối các trang, bỏ dòng trùng `id` (cảnh báo mới chen vào đẩy trang sau lùi lại). */
export function gopTrangCanhBao(trang: readonly TrangCanhBao[]): CanhBaoAudit[] {
  const daCo = new Set<string>();
  const ra: CanhBaoAudit[] = [];
  for (const p of trang) {
    for (const c of p.items) {
      if (daCo.has(c.id)) continue;
      daCo.add(c.id);
      ra.push(c);
    }
  }
  return ra;
}

/**
 * Thay bản ghi cùng `id` trong các trang đã tải bằng bản server vừa trả (sau
 * ACK), giữ nguyên con trỏ — tải lại từ đầu thì mất phần đã "Tải thêm".
 */
export function capNhatCanhBao<T extends { pages: TrangCanhBao[] }>(du: T, moi: CanhBaoAudit): T {
  return {
    ...du,
    pages: du.pages.map((p) =>
      p.items.some((c) => c.id === moi.id)
        ? { ...p, items: p.items.map((c) => (c.id === moi.id ? moi : c)) }
        : p,
    ),
  };
}
